<template>
    <div>
        <slot></slot>

        <transition
            enter-active-class="ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div class="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-if="showModal">
                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500/75 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" @click.stop="">
                        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                                    <svg class="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div class="mt-3 text-center sm:mt-2 sm:ml-4 sm:text-left">
                                    <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">Matching Participant Found</h3>
                                    <div class="mt-2 whitespace-normal">
                                        <p class="text-sm text-gray-500">
                                            A participant with matching details (date of birth and Medicare information) already exists under another referrer.
                                        </p>
                                        <p class="text-sm text-gray-500 mt-2" v-if="hasPendingRequest">
                                            A transfer request is already pending for this participant.
                                        </p>
                                        <p class="text-sm text-gray-500 mt-2" v-else>
                                            You can request a transfer of this participant to your account, or continue to create a new participant record.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button
                                v-if="!hasPendingRequest"
                                type="button"
                                @click.prevent="requestTransfer"
                                class="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                Request Transfer
                            </button>
                            <button
                                type="button"
                                @click.prevent="closeModal"
                                class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                            >
                                {{ hasPendingRequest ? 'Close' : 'Continue Anyway' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <transition
            enter-active-class="ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div class="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true" v-if="showTransferModal">
                <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 bg-gray-500/75 bg-opacity-75 transition-opacity" aria-hidden="true"></div>

                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div class="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" @click.stop="">
                        <form :action="transferUrl" method="POST">
                            <input type="hidden" name="_token" :value="csrfToken">
                            <input type="hidden" name="participant_id" :value="matchedParticipantId">

                            <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div class="sm:flex sm:items-start">
                                    <div class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                                        <svg class="h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                        </svg>
                                    </div>
                                    <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                        <h3 class="text-lg leading-6 font-medium text-gray-900" id="modal-title">Request Participant Transfer</h3>
                                        <div class="mt-4">
                                            <label for="transfer-reason" class="block text-sm font-medium text-gray-700">Reason for transfer (optional)</label>
                                            <textarea
                                                id="transfer-reason"
                                                name="reason"
                                                rows="3"
                                                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                                                placeholder="Explain why you are requesting this transfer..."
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                <button
                                    type="submit"
                                    class="w-full inline-flex justify-center rounded-md border border-transparent shadow-xs px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Submit Request
                                </button>
                                <button
                                    type="button"
                                    @click.prevent="closeTransferModal"
                                    class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
export default {
    name: "DuplicateCheck",
    props: {
        checkUrl: {
            type: String,
            required: true,
        },
        transferUrl: {
            type: String,
            required: true,
        },
        dob: {
            type: String
        },
        participantId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            showModal: false,
            showTransferModal: false,
            matchedParticipantId: null,
            hasPendingRequest: false,
            lastCheckedValues: null,
            csrfToken: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        };
    },
    mounted() {
        this.watchFields();
    },
    methods: {
        watchFields() {
            const medicareCardField = document.querySelector('input[name="medicare_card_number"]');
            const medicareRefField = document.querySelector('input[name="medicare_ref"]');

            if (!medicareCardField || !medicareRefField) {
                return;
            }

            const checkDuplicate = () => {
                const medicareCard = medicareCardField.value;
                const medicareRef = medicareRefField.value;

                if (this.dob && medicareCard && medicareRef) {
                    const currentValues = `${this.dob}|${medicareCard}|${medicareRef}`;

                    if (this.lastCheckedValues !== currentValues) {
                        this.lastCheckedValues = currentValues;
                        this.checkForDuplicate(this.dob, medicareCard, medicareRef);
                    }
                }
            };

            medicareCardField.addEventListener('blur', checkDuplicate);
            medicareRefField.addEventListener('blur', checkDuplicate);
        },

        async checkForDuplicate(dob, medicareCard, medicareRef) {
            try {
                const response = await fetch(this.checkUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.csrfToken,
                        'Accept': 'application/json',
                    },
                    body: JSON.stringify({
                        dob: dob,
                        medicare_card_number: medicareCard,
                        medicare_ref: medicareRef,
                        participant_id: this.participantId,
                    }),
                });

                const data = await response.json();

                if (data.match_found) {
                    this.matchedParticipantId = data.participant_id;
                    this.hasPendingRequest = data.has_pending_request;
                    this.showModal = true;
                }
            } catch (error) {
                console.error('Error checking for duplicate:', error);
            }
        },

        closeModal() {
            this.showModal = false;
        },

        requestTransfer() {
            this.showModal = false;
            this.showTransferModal = true;
        },

        closeTransferModal() {
            this.showTransferModal = false;
        },
    },
}
</script>

<style scoped>

</style>
